import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { pluginApi } from '@/lib/api';
import { useApiMutation } from '@/hooks/useApi';
import { Loader2, Save, ListChecks } from 'lucide-react';
import { toast } from 'sonner';

export function PollsPlugin({ serverId }: { serverId: string }) {
  const { data: plugins } = useQuery({
    queryKey: ['servers', serverId, 'plugins'],
    queryFn: () => pluginApi.getPlugins(serverId),
  });

  const pollsPlugin = (plugins as any)?.find((p: any) => p.pluginId === 'polls');
  const isEnabled = pollsPlugin?.enabled ?? false;

  const { mutate: togglePlugin } = useApiMutation(
    ({ enabled }: { enabled: boolean }) =>
      enabled ? pluginApi.enablePlugin(serverId, 'polls') : pluginApi.disablePlugin(serverId, 'polls'),
    { invalidateQueries: [['servers', serverId, 'plugins']] }
  );

  const { mutate: updateConfig, isPending: saving } = useApiMutation(
    (config: any) => pluginApi.updatePluginConfig(serverId, 'polls', config),
    {
      onSuccess: () => toast.success('Configuration saved!'),
      invalidateQueries: [['servers', serverId, 'plugins']]
    }
  );

  const [config, setConfig] = useState<any>({
    enabled: true,
    anonymousVoting: false,
    allowMultiple: false,
    maxOptions: 6,
    defaultDurationMinutes: 60
  });

  useEffect(() => {
    if (pollsPlugin?.config) {
      setConfig({ ...config, ...pollsPlugin.config });
    }
  }, [pollsPlugin]);

  if (!pollsPlugin) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <ListChecks className="w-10 h-10 mx-auto mb-3 opacity-30" />
        <p>Plugin non disponibile per questo server.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto pb-12">
      <div className="glass-card p-6 flex items-center justify-between border-b border-white/10">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-sky-500/20 rounded-xl">
            <ListChecks className="w-6 h-6 text-sky-400" />
          </div>
          <div>
            <h3 className="font-bold text-xl">Polls</h3>
            <p className="text-sm text-muted-foreground">Sondaggi con risultati live.</p>
          </div>
        </div>
        <Switch checked={isEnabled} onCheckedChange={(enabled) => togglePlugin({ enabled })} />
      </div>

      {isEnabled && (
        <>
          <div className="glass-card p-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Max opzioni</Label>
                <Input
                  type="number"
                  min="2"
                  max="10"
                  value={config.maxOptions}
                  onChange={(e) => setConfig({ ...config, maxOptions: parseInt(e.target.value) })}
                />
              </div>
              <div>
                <Label>Durata predefinita (minuti)</Label>
                <Input
                  type="number"
                  min="5"
                  max="1440"
                  value={config.defaultDurationMinutes}
                  onChange={(e) => setConfig({ ...config, defaultDurationMinutes: parseInt(e.target.value) })}
                />
              </div>
            </div>

            <div className="flex items-center justify-between p-4 rounded-lg bg-white/5">
              <div>
                <Label>Voto anonimo</Label>
                <p className="text-xs text-muted-foreground">Nasconde il nome dei votanti</p>
              </div>
              <Switch checked={config.anonymousVoting} onCheckedChange={(checked) => setConfig({ ...config, anonymousVoting: checked })} />
            </div>

            <div className="flex items-center justify-between p-4 rounded-lg bg-white/5">
              <div>
                <Label>Multi-voto</Label>
                <p className="text-xs text-muted-foreground">Consenti voto multiplo</p>
              </div>
              <Switch checked={config.allowMultiple} onCheckedChange={(checked) => setConfig({ ...config, allowMultiple: checked })} />
            </div>
          </div>

          <div className="flex justify-end">
            <Button onClick={() => updateConfig(config)} disabled={saving} className="min-w-[160px]">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save Changes
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
